import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import SpinnerOverlay from "../Components/SpinnerOverlay";
import { getProducerProfile } from "../services/producerService";

const parseDate = (dateString) => {
    if (!dateString) return null;

    const [year, month, day] = String(dateString).split("-");
    const parsedDate = new Date(Number(year), Number(month) - 1, Number(day));

    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const trimText = (text, maxLength = 160) => {
    const value = text?.trim();
    if (!value) return "";
    return value.length > maxLength ? `${value.slice(0, maxLength)}...` : value;
};

const isValidImage = (value) =>
    typeof value === "string" && value.trim() !== "" && value.trim().toLowerCase() !== "null";
/* eslint-disable react/prop-types */
const EventCard = ({ event, badgeLabel, badgeClassName, muted = false }) => {
    const eventDate = parseDate(event.fecha);
    const formattedDate = eventDate
        ? format(eventDate, "d 'de' MMMM 'de' yyyy", { locale: es })
        : "Fecha por confirmar";
    const eventTime = event.hora ? `${event.hora} hs` : "";

    return (
        <Link to={`/eventos/${event.slug}`} className="group block">
            <article
                className={`h-full overflow-hidden rounded-2xl border bg-zinc-900/75 shadow-[0_16px_40px_rgba(0,0,0,0.3)] transition duration-300 hover:-translate-y-1 hover:border-zinc-600 ${
                    muted ? "border-zinc-800/90 opacity-95" : "border-zinc-800"
                }`}
            >
                <div className="relative aspect-[16/9] overflow-hidden">
                    <img
                        src={event.img}
                        alt={event.nombre}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
                    <span
                        className={`absolute left-3 top-3 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-white shadow-sm ${badgeClassName}`}
                    >
                        {badgeLabel}
                    </span>
                    <div className="absolute bottom-3 left-3 rounded-full border border-white/15 bg-black/60 px-3 py-1 text-xs text-zinc-100 backdrop-blur-sm">
                        {event.ubicacion || "Ubicación a confirmar"}
                    </div>
                </div>

                <div className="space-y-3 p-4">
                    <div>
                        <h3 className="text-xl font-bold text-white">{event.nombre}</h3>
                        <p className="mt-1 text-sm text-zinc-300">{formattedDate}</p>
                        {eventTime ? (
                            <p className="mt-1 text-sm font-medium text-zinc-200">{eventTime}</p>
                        ) : null}
                    </div>
                    <p className="text-sm leading-relaxed text-zinc-300">
                        {trimText(event.descripcion, 120) ||
                            "Próximamente vamos a compartir más información sobre este evento."}
                    </p>
                </div>
            </article>
        </Link>
    );
};

export default function ProducerProfile() {
    const { producerSlug } = useParams();
    const [producer, setProducer] = useState(null);
    const [activeEvents, setActiveEvents] = useState([]);
    const [finishedEvents, setFinishedEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        const fetchProducer = async () => {
            setLoading(true);
            setError("");

            try {
                const response = await getProducerProfile(producerSlug);
                setProducer(response.producer);
                setActiveEvents(response.activeEvents || []);
                setFinishedEvents(response.finishedEvents || []);
            } catch (fetchError) {
                setError(fetchError.message || "No se pudo cargar la productora.");
            } finally {
                setLoading(false);
            }
        };

        fetchProducer();
    }, [producerSlug]);

    const profileImage = isValidImage(producer?.img) ? producer.img : "/avatar.jpg";
    const coverImage = "/passevent-portada.png";

    return (
        <main className="relative min-h-screen overflow-hidden bg-zinc-950 text-white">
            <div
                className="pointer-events-none absolute inset-0 opacity-80"
                aria-hidden="true"
                style={{
                    background:
                        "radial-gradient(circle at 16% 18%, rgba(255, 74, 110, 0.20), transparent 34%), radial-gradient(circle at 82% 14%, rgba(59, 130, 246, 0.14), transparent 30%), radial-gradient(circle at 54% 84%, rgba(250, 204, 21, 0.08), transparent 38%)",
                }}
            />

            <SpinnerOverlay isLoading={loading} />

            <section className="relative pt-24 sm:pt-28">
                <div className="mx-auto max-w-6xl px-4 sm:px-6">
                    <div className="overflow-hidden rounded-[2rem] border border-zinc-800/80 bg-zinc-900/70 shadow-[0_20px_60px_rgba(0,0,0,0.35)] backdrop-blur-sm">
                        <div className="relative h-[240px] sm:h-[320px]">
                            <img
                                src={coverImage}
                                alt="Portada de productora"
                                className="h-full w-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/35 to-zinc-950/10" />
                            <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_18%,rgba(255,255,255,0.10),transparent_24%),radial-gradient(circle_at_80%_12%,rgba(255,255,255,0.08),transparent_22%)]" />
                        </div>

                        <div className="relative px-4 pb-6 pt-0 sm:px-6 sm:pb-8">
                            <div className="-mt-16 flex flex-col gap-5 sm:-mt-20 lg:flex-row lg:items-end lg:justify-between">
                                <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
                                    <div className="relative h-28 w-28 overflow-hidden rounded-3xl border-4 border-zinc-950 bg-zinc-800 shadow-[0_18px_38px_rgba(0,0,0,0.35)] sm:h-36 sm:w-36">
                                        <img
                                            src={profileImage}
                                            alt={producer?.name || "Productora"}
                                            className="h-full w-full object-cover"
                                        />
                                    </div>

                                    <div className="pb-1">
                                        <span className="inline-flex rounded-full border border-rose-400/30 bg-rose-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-rose-200">
                                            Productora
                                        </span>
                                        <h1 className="mt-3 text-3xl font-black tracking-tight text-white sm:text-4xl">
                                            {producer?.name || "Productora"}
                                        </h1>
                                        {producer?.description?.trim() ? (
                                            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-zinc-300 sm:text-base">
                                                {producer.description.trim()}
                                            </p>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="relative mx-auto max-w-6xl px-4 pb-8 pt-8 sm:px-6">
                {error ? (
                    <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-6 text-center text-red-200">
                        {error}
                    </div>
                ) : null}

                {!loading && !error ? (
                    <>
                        <div className="mb-6 flex items-end justify-between gap-4">
                            <div>
                                <p className="text-sm font-semibold uppercase tracking-[0.14em] text-rose-300">
                                    Programación
                                </p>
                                <h2 className="mt-2 text-2xl font-black text-white sm:text-3xl">
                                    Eventos activos
                                </h2>
                            </div>
                            <Link
                                to="/"
                                className="hidden rounded-xl border border-zinc-700 bg-zinc-900/70 px-4 py-2 text-sm font-semibold text-zinc-200 transition hover:border-zinc-500 hover:text-white sm:inline-flex"
                            >
                                Ver cartelera
                            </Link>
                        </div>

                        {activeEvents.length > 0 ? (
                            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
                                {activeEvents.map((event) => (
                                    <EventCard
                                        key={event.id}
                                        event={event}
                                        badgeLabel="Activo"
                                        badgeClassName="bg-emerald-500/85"
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-8 text-center text-zinc-300">
                                No hay eventos activos para mostrar.
                            </div>
                        )}

                        <div className="mb-6 mt-12">
                            <p className="text-sm font-semibold uppercase tracking-[0.14em] text-rose-300">
                                Historial
                            </p>
                            <h2 className="mt-2 text-2xl font-black text-white sm:text-3xl">
                                Eventos finalizados
                            </h2>
                        </div>

                        {finishedEvents.length > 0 ? (
                            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
                                {finishedEvents.map((event) => (
                                    <EventCard
                                        key={event.id}
                                        event={event}
                                        badgeLabel="Finalizado"
                                        badgeClassName="bg-zinc-700/90"
                                        muted
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-8 text-center text-zinc-300">
                                No hay eventos finalizados para mostrar.
                            </div>
                        )}
                    </>
                ) : null}
            </section>
        </main>
    );
}
